import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Card, Chip, CircularProgress, Input, Sheet, Stack, Table, Typography } from '@mui/joy';
import { DELIVERY_TO_LABELS, PRODUCTION_PAID_LABELS } from '../components/ProjectWorkflow';
import { directSupabase } from '../lib/supabaseClient';
import { latestInstallEventsByProject, type InstallEventRow } from './project-workspace/fitterJobList';
import { formatShortDay, toInstallDateOnly, todayInstallDateOnly } from './project-workspace/jobCardInstallDate';
import {
  awaitingProductionRows,
  finalAcceptedProjectIds,
  isOpenProject,
  productionMatches,
  productionRows,
  sectionProductionRows,
  type FinalDeltaRow,
  type FinalSurveyRow,
  type ProductionProject,
  type ProductionRow,
} from './project-workspace/productionDates.model';

function chunks<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Workspaces with an accepted final survey (complete, and no reapproval needed or the customer accepted). */
async function loadFinalAcceptedIds(): Promise<Set<string>> {
  const { data: surveyData, error: surveyError } = await (directSupabase as any)
    .from('visual_designer_surveys')
    .select('id, project_id, option_id, completion_status')
    .eq('kind', 'final')
    .eq('completion_status', 'complete');
  if (surveyError) throw new Error(surveyError.message);
  const surveys = (surveyData || []) as FinalSurveyRow[];
  const deltas: FinalDeltaRow[] = [];
  for (const ids of chunks(surveys.map((survey) => survey.id), 100)) {
    const { data, error } = await (directSupabase as any)
      .from('final_survey_deltas')
      .select('final_survey_id, classification, customer_accepted_at, reapproval_transmission_version_id, completed_at')
      .in('final_survey_id', ids)
      .is('superseded_at', null);
    if (error) throw new Error(error.message);
    deltas.push(...((data || []) as FinalDeltaRow[]));
  }
  return finalAcceptedProjectIds(surveys, deltas);
}

async function loadProductionRows(): Promise<ProductionRow[]> {
  const [{ data: projectData, error: projectError }, finalAccepted] = await Promise.all([
    directSupabase
      .from('projects')
      .select('id, project_name, customer_name, lifecycle_ref, supply_only, project_status, complete, workflow_tasks'),
    loadFinalAcceptedIds(),
  ]);
  if (projectError) throw new Error(projectError.message);
  const projects = ((projectData || []) as unknown as ProductionProject[]).filter((project) => isOpenProject(project) && (
    finalAccepted.has(project.id)
    || (Array.isArray(project.workflow_tasks)
      && project.workflow_tasks.some((task) => task?.task === 'Production Date' && (task.productionDates || []).length > 0))
  ));
  const projectIds = projects.map((project) => project.id);

  const { data: supplierData } = await directSupabase.from('suppliers').select('id, supplier_name');
  const eventRows: Array<InstallEventRow & { event_type: string }> = [];
  for (const ids of chunks(projectIds, 100)) {
    const { data, error } = await directSupabase
      .from('project_events')
      .select('project_id, event_type, event_date, end_date, metadata, status, created_at')
      .in('project_id', ids)
      .in('event_type', ['installationDateConfirmation', 'deliveryDateConfirmation']);
    if (error) throw new Error(error.message);
    eventRows.push(...((data || []) as unknown as Array<InstallEventRow & { event_type: string }>));
  }
  const suppliers = { data: supplierData };
  const events = { data: eventRows };
  const supplierNames = new Map<string, string>(
    ((suppliers.data || []) as Array<{ id: string; supplier_name: string | null }>)
      .map((row) => [String(row.id), String(row.supplier_name || 'Supplier')]),
  );
  // Installs for fitted jobs, deliveries for supply-only ones
  const supplyOnly = new Map(projects.map((project) => [project.id, !!project.supply_only]));
  const booked = ((events.data || []) as Array<InstallEventRow & { event_type: string }>).filter((row) => (
    row.event_type === (supplyOnly.get(row.project_id) ? 'deliveryDateConfirmation' : 'installationDateConfirmation')
  ));
  const bookedDay = new Map<string, string>();
  latestInstallEventsByProject(booked).forEach((event, projectId) => {
    const day = toInstallDateOnly(event.event_date);
    if (day) bookedDay.set(projectId, day);
  });
  return [
    ...awaitingProductionRows(projects, finalAccepted, bookedDay),
    ...productionRows(projects, supplierNames, bookedDay),
  ];
}

const PAID_COLOR = { Fully: 'success', Deposit: 'warning' } as const;

/** Office: supplier production dates across open workspaces, with the install date beside each. */
const ProductionDatesPage: React.FC = () => {
  const [rows, setRows] = useState<ProductionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const today = todayInstallDateOnly();

  useEffect(() => {
    let cancelled = false;
    loadProductionRows()
      .then((next) => { if (!cancelled) setRows(next); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load production dates'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sections = useMemo(
    () => sectionProductionRows(rows.filter((row) => productionMatches(row, search)), today),
    [rows, search, today],
  );
  const clashes = rows.filter((row) => row.afterBooking && row.date && row.date >= today).length;

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: { xs: 1, md: 2 }, width: '100%', maxWidth: 1200, mx: 'auto', boxSizing: 'border-box' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} gap={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography level="h3">Production Dates</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Supplier production dates across open workspaces, with each job&apos;s install or delivery date. Set them on the workspace&apos;s Supplier stage.
          </Typography>
        </Box>
        <Input
          size="sm"
          placeholder="Search customer, job ref, product, supplier or their ref"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: { sm: 320 } }}
        />
      </Stack>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
      {clashes > 0 && (
        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
          {clashes} upcoming production date{clashes === 1 ? ' is' : 's are'} after the job&apos;s install or delivery date.
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size="sm" /></Stack>
      ) : sections.length === 0 ? (
        <Card variant="outlined">
          <Typography level="body-sm" sx={{ color: 'neutral.500', textAlign: 'center', py: 2 }}>
            {search.trim() ? 'No production dates match that search.' : 'No production dates set on open workspaces.'}
          </Typography>
        </Card>
      ) : (
        <Stack gap={2.5}>
          {sections.map((section) => (
            <Box key={section.id}>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography level="title-lg">{section.title}</Typography>
                <Chip size="sm" variant="soft">{section.rows.length}</Chip>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{section.note}</Typography>
              </Stack>
              <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                <Table size="sm" hoverRow sx={{ minWidth: 820, tableLayout: 'fixed', '& td': { verticalAlign: 'top', py: 1.25 } }}>
                  <thead>
                    <tr>
                      <th style={{ width: '13%' }}>Production</th>
                      <th style={{ width: '27%' }}>Workspace</th>
                      <th style={{ width: '24%' }}>Product · supplier</th>
                      <th style={{ width: '10%' }}>Paid</th>
                      <th style={{ width: '16%' }}>Install</th>
                      <th style={{ width: '10%' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <Typography level="title-sm" color={section.id === 'recent' ? 'neutral' : undefined}>
                            {row.date ? formatShortDay(row.date) : '—'}
                          </Typography>
                          {row.date && row.provisional && (
                            <Chip size="sm" variant="soft" color="warning">Provisional</Chip>
                          )}
                        </td>
                        <td>
                          <Typography level="title-sm">
                            {row.customer}
                            {row.ref && <Typography level="body-xs" textColor="neutral.500" sx={{ ml: 1 }}>{row.ref}</Typography>}
                          </Typography>
                          {row.workspace && row.workspace !== row.customer && (
                            <Typography level="body-xs" noWrap>{row.workspace}</Typography>
                          )}
                        </td>
                        <td>
                          {row.awaiting ? (
                            <Chip size="sm" variant="soft" color="warning">Not ordered yet</Chip>
                          ) : (
                            <>
                              <Typography level="body-sm">
                                {row.productType}
                                {row.reference && <Typography level="body-xs" textColor="neutral.500" sx={{ ml: 1 }}>{row.reference}</Typography>}
                              </Typography>
                              <Typography level="body-xs" textColor="neutral.500">
                                {row.supplier}
                                {row.deliveryTo ? ` · to ${DELIVERY_TO_LABELS[row.deliveryTo].toLowerCase()}` : ''}
                              </Typography>
                            </>
                          )}
                        </td>
                        <td>
                          {row.awaiting ? null : row.paid
                            ? <Chip size="sm" variant="soft" color={PAID_COLOR[row.paid]}>{PRODUCTION_PAID_LABELS[row.paid]}</Chip>
                            : <Typography level="body-xs" textColor="neutral.500">None</Typography>}
                        </td>
                        <td>
                          <Typography level="body-sm">
                            {row.bookedDay ? `${row.supplyOnly ? 'Delivery' : 'Install'} ${formatShortDay(row.bookedDay)}` : 'Not booked'}
                          </Typography>
                          {row.afterBooking && <Chip size="sm" variant="soft" color="danger">After {row.supplyOnly ? 'delivery' : 'install'} date</Chip>}
                        </td>
                        <td>
                          <Typography
                            level="body-sm"
                            fontWeight="lg"
                            component={RouterLink}
                            to={`/projects/${row.projectId}/workspace/supplier`}
                            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            Open workspace
                          </Typography>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ProductionDatesPage;
